import { AbstractStore, ActionHandlers, BaseStoreAction } from './abstract.store';

export type CurrentlyTypingStoreState = {
    currentlyTyping: {
        [conversationId: number]: Set<number>;
    };
};

export class ChatCurrentlyTypingStore extends AbstractStore<BaseStoreAction, CurrentlyTypingStoreState> {
    static $inject = [];

    private currentlyTypingEvents$: Rx.Subject<{ userId: number; meetingId: number }> = new Rx.Subject<{
        userId: number;
        meetingId: number;
    }>();

    constructor() {
        super();

        // - GroupBy creates an observable for each userId:meetingId that it encounters
        // - Then, each observable is debounced separately so that we can spam "USER IS TYPING" events, and we get
        //   a "user is not typing any more" 3 seconds after the last "user is typing" events for each user:meeting pair
        // - Then, we just merge those observables so that we have one source of "user is not typing any more"
        //   events

        this.currentlyTypingEvents$
            .groupBy((v) => `${v.meetingId}:${v.userId}`)
            .map((v) => v.debounce(3000))
            .mergeAll()
            .subscribe((v) => {
                this.dispatchUserStoppedTypingInMeeting(v.userId, v.meetingId);
            });
    }

    createInitialState(): CurrentlyTypingStoreState {
        return {
            currentlyTyping: [],
        };
    }

    usersTypingInMeeting$(meetingId: number) {
        return this.store$.map((s) => s.currentlyTyping[meetingId] ?? new Set<number>());
    }

    dispatchUserIsTypingInMeeting(userId: number, meetingId: number) {
        this.dispatch({
            type: 'user_is_typing',
            payload: { userId, meetingId },
        });
    }

    dispatchUserStoppedTypingInMeeting(userId: number, meetingId: number) {
        this.dispatch({
            type: 'user_stopped_typing',
            payload: { userId, meetingId },
        });
    }

    getActionHandlers(): ActionHandlers<BaseStoreAction, CurrentlyTypingStoreState> {
        return {
            user_is_typing: (state, payload) => {
                const meetingId = <number>payload.meetingId;
                const currentlyTyping = state.currentlyTyping[meetingId] ?? new Set<number>();

                const newState = { ...state };
                newState.currentlyTyping[meetingId] = new Set<number>([...currentlyTyping, payload.userId]);

                this.currentlyTypingEvents$.onNext(payload);

                return newState;
            },
            user_stopped_typing: (state, payload) => {
                const meetingId = <number>payload.meetingId;
                const currentlyTyping = state.currentlyTyping[meetingId] ?? new Set<number>();

                const newState = { ...state };

                newState.currentlyTyping[meetingId] = new Set<number>(currentlyTyping);
                newState.currentlyTyping[meetingId].delete(payload.userId);

                return newState;
            },
        };
    }
}

angular.module('tradersyard').service('ChatCurrentlyTypingStore', ChatCurrentlyTypingStore);
